import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stethoscope, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2, X, Plus, Filter, Sparkles, Award, AlertOctagon, Eye, Target, Save, FileDown, Zap, ArrowRight } from "lucide-react";
import { TableVirtuoso } from "react-virtuoso";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { BROKERS, formatMoney } from "../lib/constants";
import api from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import BehavioralBiasCards from "../components/dashboard/BehavioralBiasCards";
import ConfirmMappingPanel from "../components/dashboard/ConfirmMappingPanel";
import { TradeHealthReportHero } from "./TradeDoctor/HeroSection";
import { FixTheseFirstCards } from "./TradeDoctor/FixTheseFirst";
import { StrengthsCards } from "./TradeDoctor/Strengths";
import { StrengthsDiagnosticDrawer } from "./TradeDoctor/StrengthsDiagnosticDrawer";
import { toast } from "sonner";

import DataConfidencePill from "../components/DataConfidencePill";
import PaywallModal from "../components/paywall/PaywallModal";

// Iter 41 — Trade Doctor: full multi-file CSV ingestion pipeline.
// Step 1 (Iter 40) wired up the tab + single-file upload UI.
// This iteration extends the flow to:
//   • Multi-file upload — each file gets its own broker dropdown.
//   • Parsing for Delta Exchange (other brokers return "Parser coming soon").
//   • Auto-tagging (broker + asset_class) and currency normalisation to USD.
//   • Combined preview table + asset-class / broker filter.
// AI analysis & scoring are deliberately deferred to the next iteration.

// Per-broker CSV export instructions. Single source of truth — only the
// broker name in the heading changes per row.
const BROKER_INSTRUCTIONS = {
  "Delta Exchange":  "Login → Wallet → Trade History → set the date range → click 'Download CSV'.",
  "CoinDCX":         "Login → Account → Reports → Trade History → choose a period → 'Export CSV'.",
  "Binance":         "Login → Orders → Spot/Futures Order History → 'Export' → choose CSV and a date range.",
  "Exness":          "Personal Area → Trading History → choose account → 'Export to CSV' (MT4/MT5 statements also work).",
  "XM":              "Member Area → My Accounts → Trade History → 'Export Detailed Statement' → save as CSV.",
  "IC Markets":      "Secure Client Area → My Accounts → Statement → choose period → 'Export to CSV'.",
  "Pepperstone":     "Secure Client Area → Account → Trade History → 'Download CSV'.",
  "FTMO":            "Client Area → Account Metrix → Trading Statement → 'Download CSV / Detailed Report'.",
  "Funding Pips":    "Dashboard → Account → Trading History → 'Export CSV'.",
  "FundedNext":      "Client Area → My Accounts → choose account → 'Trading History' → 'Export CSV'.",
  "The5%ers":        "Dashboard → My Accounts → Trading Statement → 'Download CSV'.",
  "Zerodha":         "Console → Reports → Tradebook → choose segment + date range → 'Download CSV'.",
  "Upstox":          "Upstox Pro → Reports → Trade Book → select date range → 'Download CSV'.",
  "Groww":           "Profile → Reports → Trade Statement → choose period → 'Download CSV'.",
  "Angel One":       "Angel One web → Reports → Trade Book → choose segment + date range → 'Download CSV'.",
  "ICICI Direct":    "Customer Service → Reports → Trade Book → choose date range → 'Export to CSV'.",
  "HDFC Securities": "Login → Portfolio → Trade Book → choose date range → 'Download CSV'.",
};

const MAX_BYTES = 10 * 1024 * 1024;

// React-key generator — `crypto.randomUUID` not always available in older
// previews so fall back to a timestamp-based id.
const newId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const ASSET_CLASS_LABEL = {
  crypto: "Crypto",
  "indian-equity": "Indian Equity",
  forex: "Forex",
  indices: "Indices",
  other: "Other",
};

export default function TradeDoctor() {
  // `files` is an array of { id, file, broker, status, result, error }.
  //   status: "pending" | "parsing" | "done" | "unsupported" | "error"
  const [files, setFiles] = useState([]);
  const [defaultBroker, setDefaultBroker] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [phase, setPhase] = useState("upload"); // "upload" | "results"
  const [filter, setFilter] = useState("all");
  // Iter 63 — "Re-detect (skip cache)" toggle. When ON, the next parse
  // run will bypass the saved per-user column-mapping cache and force the
  // backend to re-run the AI mapper (or refresh the deterministic
  // fast-path). Useful after a parser update or when an earlier broken
  // mapping was cached.
  const [forceRefresh, setForceRefresh] = useState(false);
  // Iter 53 — current language (drives Hinglish vs English LLM narration).
  const { language } = useLanguage();
  // Iter 42 — AI audit state.
  const [auditState, setAuditState] = useState("idle"); // "idle" | "loading" | "done" | "error"
  const [auditData, setAuditData] = useState(null);     // { aggregates, report } | null
  const [auditError, setAuditError] = useState(null);
  // Iter 43 — bulk-import state.
  const [importState, setImportState] = useState("idle"); // "idle" | "loading" | "done"
  const [importResult, setImportResult] = useState(null); // { inserted, skipped_existing, skipped_invalid, total }
  const inputRef = useRef(null);
  const nav = useNavigate();

  // ── Iter 48: session persistence ──
  // Restore last results-phase state on mount (after a refresh / nav away
  // and back). Only the results phase is persisted — File objects can't be
  // serialised so we never persist the upload phase. Storage stays per-tab
  // (sessionStorage) so closing the tab is a clean reset.
  const SESSION_KEY = "td_session_v1";
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) return;
      const s = JSON.parse(raw);
      if (s?.phase !== "results") return;
      setFiles(Array.isArray(s.files) ? s.files : []);
      setPhase("results");
      setFilter(s.filter || "all");
      setAuditState(s.auditState || "idle");
      setAuditData(s.auditData || null);
      setAuditError(s.auditError || null);
      setImportState(s.importState || "idle");
      setImportResult(s.importResult || null);
      setDefaultBroker(s.defaultBroker || "");
    } catch {
      sessionStorage.removeItem(SESSION_KEY);
    }
  }, []);
  // Persist whenever results-phase state changes. Strip the live File
  // objects to a small {name, size} shape since they can't be revived
  // from JSON anyway (the parsed `result` is what we actually need).
  useEffect(() => {
    if (!restoredRef.current) return;
    if (phase !== "results") {
      sessionStorage.removeItem(SESSION_KEY);
      return;
    }
    try {
      const payload = {
        phase,
        defaultBroker,
        filter,
        auditState: auditState === "loading" ? "idle" : auditState,
        auditData,
        auditError,
        importState: importState === "loading" ? "idle" : importState,
        importResult,
        files: files.map((r) => ({
          id: r.id,
          broker: r.broker,
          status: r.status === "parsing" ? "done" : r.status,
          result: r.result,
          error: r.error,
          file: r.file
            ? { name: r.file.name || "uploaded.csv", size: r.file.size || 0 }
            : null,
        })),
      };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(payload));
    } catch {
      /* quota — ignore, app continues to work without persistence */
    }
  }, [phase, defaultBroker, filter, auditState, auditData, auditError, importState, importResult, files]);

  // ── File acceptance ────────────────────────────────────────────────
  const acceptFiles = (incoming) => {
    const list = Array.from(incoming || []);
    if (!list.length) return;
    const accepted = [];
    for (const f of list) {
      const isCsv =
        f.type === "text/csv" ||
        f.type === "application/vnd.ms-excel" ||
        f.name.toLowerCase().endsWith(".csv");
      if (!isCsv) {
        toast.error(`${f.name}: only .csv files are supported.`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name}: larger than 10 MB.`);
        continue;
      }
      accepted.push({
        id: newId(),
        file: f,
        broker: defaultBroker,
        status: "pending",
        result: null,
        error: null,
      });
    }
    if (accepted.length) {
      setFiles((prev) => [...prev, ...accepted]);
    }
  };

  const onPickClick = () => inputRef.current?.click();
  const onFileInput = (e) => {
    acceptFiles(e.target.files);
    e.target.value = "";
  };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    acceptFiles(e.dataTransfer?.files);
  };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); if (!dragOver) setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };

  const updateRow = (id, patch) =>
    setFiles((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeRow = (id) =>
    setFiles((prev) => prev.filter((r) => r.id !== id));

  const onDefaultBrokerChange = (v) => {
    setDefaultBroker(v);
    // Apply to any existing pending rows that don't already have a broker.
    setFiles((prev) =>
      prev.map((r) => (r.broker ? r : { ...r, broker: v }))
    );
  };

  const allReady = files.length > 0 && files.every((f) => f.broker);
  const anyParsing = files.some((f) => f.status === "parsing");

  // ── Submit all files in parallel to the parse endpoint ────────────
  const onAnalyseAll = async () => {
    if (!allReady || anyParsing) return;
    setPhase("results");
    // New parse run invalidates any prior audit.
    setAuditState("idle");
    setAuditData(null);
    setAuditError(null);
    setImportState("idle");
    setImportResult(null);
    // Reset any prior status flags
    setFiles((prev) => prev.map((r) => ({ ...r, status: "parsing", result: null, error: null })));

    const tasks = files.map(async (row) => {
      const fd = new FormData();
      fd.append("file", row.file);
      fd.append("broker", row.broker);
      if (forceRefresh) fd.append("force_refresh", "true");
      try {
        // axios via shared api client — auto-sends Bearer token & uses
        // REACT_APP_BACKEND_URL prefix.
        const { data } = await api.post("/trade-doctor/parse", fd, {
          // Don't set Content-Type — let axios add the multipart boundary.
          headers: { "Content-Type": "multipart/form-data" },
        });
        updateRow(row.id, { status: data.status === "ok" ? "done" : data.status, result: data, error: null });
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || "Parse failed";
        updateRow(row.id, { status: "error", error: msg });
      }
    });
    await Promise.all(tasks);
  };

  const onStartOver = () => {
    setFiles([]);
    setPhase("upload");
    setFilter("all");
    setAuditState("idle");
    setAuditData(null);
    setAuditError(null);
    setImportState("idle");
    setImportResult(null);
    try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
  };

  // ── Iter 43: Bulk-import the parsed trades into the user's journal ──
  // Iter 129 — preview-before-commit: hit `/preview-import` first, show
  // a confirmation dialog with new/will-skip counts + sample matches,
  // then actually POST `/import` only after the user confirms.
  const [importPreview, setImportPreview] = React.useState(null);      // {new, will_skip, invalid, total, sample}
  const [pendingImportPayload, setPendingImportPayload] = React.useState(null);

  const onImportToJournal = async (tradesToImport) => {
    if (!tradesToImport?.length) return;
    setImportState("loading");
    try {
      const { data: preview } = await api.post(
        "/trade-doctor/preview-import",
        { trades: tradesToImport },
      );
      // Skip the confirmation dialog when there's literally nothing
      // interesting to preview (all-new, small batch) — go straight
      // to the commit. Keeps first-time-import UX identical to before.
      if (preview.will_skip === 0 && preview.invalid === 0) {
        await _commitImport(tradesToImport);
        return;
      }
      setImportPreview(preview);
      setPendingImportPayload(tradesToImport);
      setImportState("idle");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Import preview failed";
      toast.error(msg);
      setImportState("idle");
    }
  };

  const _commitImport = async (tradesToImport) => {
    setImportState("loading");
    setImportPreview(null);
    setPendingImportPayload(null);
    try {
      const { data } = await api.post("/trade-doctor/import", { trades: tradesToImport });
      setImportResult(data);
      setImportState("done");
      const { inserted, skipped_existing, skipped_invalid } = data;
      if (inserted > 0) {
        const skippedNote = skipped_existing
          ? ` · ${skipped_existing} already in journal`
          : "";
        toast.success(`${inserted} trade${inserted === 1 ? "" : "s"} added to your journal${skippedNote}.`);
      } else if (skipped_existing > 0) {
        toast.info(`All ${skipped_existing} trades are already in your journal.`);
      } else {
        toast.warning(`No trades imported (${skipped_invalid} invalid).`);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Import failed";
      toast.error(msg);
      setImportState("idle");
    }
  };

  const onConfirmImport = () => {
    if (pendingImportPayload) _commitImport(pendingImportPayload);
  };

  const onCancelImport = () => {
    setImportPreview(null);
    setPendingImportPayload(null);
    setImportState("idle");
  };

  // ── Iter 42: Generate the AI audit from the parsed (and currency-
  // normalised) trades. Sends the *filtered* slice so the user can audit
  // a specific asset class / broker subset if they want.
  // Iter 53 — accepts an optional `langOverride` so the "Regenerate in
  // <other lang>" CTA can pass the toggle's current value explicitly.
  // Iter 98 — accepts a `forceRegenerate` flag so the "Regenerate
  // narrative" button can bypass the SHA256-keyed audit cache.
  // D7b — Paywall trigger state. If audit returns 402 with
  // `trigger="doctor_report_second"`, we show the reusable
  // PaywallModal instead of a red error toast — the user just used
  // their one free credit and this is where they'd naturally see
  // the upgrade CTA.
  const [paywallOpen, setPaywallOpen] = useState(false);

  const onGenerateAudit = async (tradesToAudit, langOverride, forceRegenerate = false) => {
    if (!tradesToAudit?.length) return;
    setAuditState("loading");
    setAuditError(null);
    setAuditData(null);
    try {
      const { data } = await api.post("/trade-doctor/audit", {
        trades: tradesToAudit,
        language: langOverride || language,
        regenerate: !!forceRegenerate,
      });
      setAuditData(data);
      setAuditState("done");
    } catch (err) {
      // D7b — 402 = "free doctor credit already spent". Open the
      // contextual paywall. The reusable PaywallModal fires
      // /entitlements/paywall-shown itself on mount, so no manual
      // fire needed here.
      if (err?.response?.status === 402
          && err?.response?.data?.detail?.trigger === "doctor_report_second") {
        setAuditState("idle");
        setPaywallOpen(true);
        return;
      }
      const msg = err?.response?.data?.detail || err?.message || "Audit failed";
      setAuditError(msg);
      setAuditState("error");
      toast.error(msg);
    }
  };

  // ── Aggregate parsed trades across files + apply filter ────────────
  // Iter 89 — assign a STABLE `_cid` (client-id) to every aggregated
  // trade so virtualization, click-through, and de-duplication don't
  // depend on field-value matching. Two visually-identical rows (e.g.
  // duplicate scalps) still get distinct ids because the seed includes
  // (file-index, trade-index) which are unique by construction.
  const allTrades = useMemo(() => {
    const out = [];
    files.forEach((row, fi) => {
      const t = row.result?.trades;
      if (!t || !Array.isArray(t)) return;
      t.forEach((tr, ti) => {
        out.push({
          ...tr,
          _sourceFile: row.file.name,
          _cid: `cid-${fi}-${ti}`,
        });
      });
    });
    return out;
  }, [files]);

  // Iter 89 → 108 — symbol search filter. Iter 108 upgrades from
  // case-insensitive SUBSTRING match to TOKEN/EXACT match: typing
  // "HUSD" now matches ONLY HUSD (not ETHUSD). Multiple symbols can be
  // supplied comma/space-separated for OR-combined filtering
  // (e.g. "HUSD, ETHUSD" → matches both).
  const [symbolFilter, setSymbolFilter] = useState("");

  // Iter 108 — "Trust this trade" toggle. When the runtime multiplier-
  // drift detector flags a symbol, the user can flip that symbol off so
  // its trades stop tainting the audit until they've verified the
  // contract spec with Delta. Set of UPPERCASE symbol names — trades
  // whose symbol is in this set are excluded from `filteredTrades`
  // (which flows into the totals, the audit, the PDF, everything).
  const [excludedSymbols, setExcludedSymbols] = useState(() => new Set());

  const toggleSymbolTrust = (sym) => {
    if (!sym) return;
    const s = String(sym).toUpperCase();
    setExcludedSymbols((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  // Build filter options dynamically from the active asset classes + brokers
  const filterOptions = useMemo(() => {
    const classes = new Set();
    const brokerSet = new Set();
    for (const tr of allTrades) {
      if (tr.asset_class) classes.add(tr.asset_class);
      if (tr.broker) brokerSet.add(tr.broker);
    }
    const opts = [{ value: "all", label: `All Combined (${allTrades.length})` }];
    for (const c of ["crypto", "indian-equity", "forex", "indices", "other"]) {
      if (classes.has(c)) {
        const count = allTrades.filter((t) => t.asset_class === c).length;
        opts.push({ value: `class:${c}`, label: `${ASSET_CLASS_LABEL[c] || c} only (${count})` });
      }
    }
    for (const b of brokerSet) {
      const count = allTrades.filter((t) => t.broker === b).length;
      opts.push({ value: `broker:${b}`, label: `${b} (${count})` });
    }
    return opts;
  }, [allTrades]);

  const filteredTrades = useMemo(() => {
    // 1. Broker / asset-class filter (existing).
    let base;
    if (filter === "all") base = allTrades;
    else if (filter.startsWith("class:")) {
      const c = filter.slice(6);
      base = allTrades.filter((t) => t.asset_class === c);
    } else if (filter.startsWith("broker:")) {
      const b = filter.slice(7);
      base = allTrades.filter((t) => t.broker === b);
    } else {
      base = allTrades;
    }
    // 2. Iter 108 — "Trust this trade" exclusion (drift-flagged symbols
    // the user has opted out of). Applied BEFORE the free-text filter
    // so a manually-searched symbol still respects the trust setting.
    if (excludedSymbols.size > 0) {
      base = base.filter(
        (t) => !excludedSymbols.has((t.symbol || "").toUpperCase()),
      );
    }
    // 3. Iter 108 — TOKEN/EXACT symbol filter (was substring pre-108).
    // Query is uppercased and split on commas / whitespace; each token
    // must EXACTLY equal a symbol. OR-combined across tokens.
    const q = (symbolFilter || "").trim().toUpperCase();
    if (!q) return base;
    const tokens = q.split(/[\s,]+/).filter(Boolean);
    if (tokens.length === 0) return base;
    const tokenSet = new Set(tokens);
    return base.filter((t) => tokenSet.has((t.symbol || "").toUpperCase()));
  }, [allTrades, filter, symbolFilter, excludedSymbols]);

  // ── Renders ────────────────────────────────────────────────────────
  return (
    <div data-testid="trade-doctor-page" className="space-y-8 max-w-5xl">
      <div className="space-y-2">
        <p className="bt-label" style={{ color: "var(--bt-accent)" }}>Beta</p>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h1 className="font-heading text-3xl sm:text-4xl flex items-center gap-3">
            <Stethoscope className="w-7 h-7" style={{ color: "var(--bt-accent)" }} />
            Trade Doctor
          </h1>
          {/* iter122 — data confidence pill: at-a-glance signal that
              the numbers below are trustworthy (or actionable). */}
          <DataConfidencePill testid="trade-doctor-confidence-pill" />
        </div>
        <p className="text-sm text-[var(--bt-text-muted)] max-w-2xl">
          Upload one or more broker CSV statements. We parse, tag and
          normalise everything to USD so we can spot patterns across all
          your accounts in one place.
        </p>
      </div>

      {phase === "upload" ? (
        <>
          <DeltaSyncBanner />
          <UploadPhase
            files={files}
            defaultBroker={defaultBroker}
            onDefaultBrokerChange={onDefaultBrokerChange}
            onPickClick={onPickClick}
            onFileInput={onFileInput}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            dragOver={dragOver}
            inputRef={inputRef}
            updateRow={updateRow}
            removeRow={removeRow}
            allReady={allReady}
            onAnalyseAll={onAnalyseAll}
            forceRefresh={forceRefresh}
            setForceRefresh={setForceRefresh}
          />
        </>
      ) : (
        <ResultsPhase
          files={files}
          setFiles={setFiles}
          allTrades={allTrades}
          filteredTrades={filteredTrades}
          filterOptions={filterOptions}
          filter={filter}
          setFilter={setFilter}
          symbolFilter={symbolFilter}
          setSymbolFilter={setSymbolFilter}
          excludedSymbols={excludedSymbols}
          toggleSymbolTrust={toggleSymbolTrust}
          anyParsing={anyParsing}
          onStartOver={onStartOver}
          auditState={auditState}
          auditData={auditData}
          auditError={auditError}
          onGenerateAudit={onGenerateAudit}
          language={language}
          importState={importState}
          importResult={importResult}
          onImportToJournal={onImportToJournal}
          onGoToJournal={() => nav("/trades")}
        />
      )}

      {/* Iter 129 — preview-before-commit dialog. Rendered once on
          the page; opens when `importPreview` is set (i.e. after the
          user clicked "Import to journal" AND the preview endpoint
          reported ≥1 will_skip or invalid row). */}
      {importPreview && (
        <ImportPreviewDialog
          preview={importPreview}
          onCancel={onCancelImport}
          onConfirm={onConfirmImport}
        />
      )}
      {/* D7b — Contextual paywall opened when the audit endpoint
          returns 402 with trigger="doctor_report_second". Reusable
          modal handles copy + CTA + /pricing routing. */}
      <PaywallModal
        open={paywallOpen}
        onOpenChange={setPaywallOpen}
        trigger="doctor_report_second"
      />
    </div>
  );
}

// ── Iter 129: import preview dialog ──────────────────────────────────
function ImportPreviewDialog({ preview, onCancel, onConfirm }) {
  const { new: n_new, will_skip, invalid, total, sample } = preview || {};
  return (
    <div
      data-testid="import-preview-backdrop"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)" }}
      onClick={onCancel}
    >
      <div
        data-testid="import-preview-dialog"
        className="bt-card max-w-lg w-full p-6 space-y-4"
        style={{ background: "var(--bt-surface)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <p className="bt-label">Review before importing</p>
          <h2 className="font-heading text-2xl mt-1">
            {n_new > 0 ? `${n_new} new` : "Nothing new"}
            {will_skip > 0 && (
              <span className="text-[var(--bt-text-muted)] text-lg ml-2">
                · {will_skip} already in your journal
              </span>
            )}
            {invalid > 0 && (
              <span className="text-[var(--bt-warn)] text-lg ml-2">
                · {invalid} invalid
              </span>
            )}
          </h2>
          <p className="text-xs text-[var(--bt-text-muted)] mt-1">
            of <span className="bt-num">{total}</span> parsed trade{total === 1 ? "" : "s"}
          </p>
        </div>

        {will_skip > 0 && sample?.length > 0 && (
          <div
            className="rounded-sm border p-3"
            style={{ borderColor: "var(--bt-border)", background: "var(--bt-surface-hover)" }}
          >
            <p className="text-xs uppercase tracking-wider text-[var(--bt-text-muted)]">
              Sample of duplicates (max 5)
            </p>
            <ul className="mt-2 space-y-1 text-sm" data-testid="import-preview-sample">
              {sample.map((s, i) => (
                <li key={`${s.existing_id || i}`} className="flex justify-between gap-3">
                  <span className="font-mono">
                    {s.symbol}{" "}
                    <span className="text-[var(--bt-text-muted)]">· {s.date || "—"}</span>
                  </span>
                  <span className="text-xs text-[var(--bt-text-muted)]">
                    matches {s.existing_data_source || "existing"} ·{" "}
                    {s.match_type === "fingerprint" ? "exact" :
                     s.match_type === "fuzzy_timestamp" ? "±2s time match" :
                     "existing import"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="text-sm text-[var(--bt-text-muted)] leading-relaxed">
          Duplicates and invalid rows will be skipped. Only the{" "}
          <span className="bt-num font-semibold text-[var(--bt-text)]">{n_new}</span>{" "}
          genuinely new trade{n_new === 1 ? "" : "s"} will be added to your journal.
        </p>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            data-testid="import-preview-cancel"
            onClick={onCancel}
            className="px-4 py-2 text-sm rounded-md border"
            style={{ borderColor: "var(--bt-border)", color: "var(--bt-text)" }}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="import-preview-confirm"
            onClick={onConfirm}
            disabled={n_new === 0}
            className="px-4 py-2 text-sm rounded-md font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: "var(--bt-accent)", color: "#000" }}
          >
            {n_new > 0 ? `Import ${n_new} trade${n_new === 1 ? "" : "s"}` : "Nothing to import"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Fix 2 (2026-09-16) — CSV import audit trail ──────────────────────
// Per-symbol summary (2a) with click-to-expand per-trade detail (2b) and
// open-position messaging (2c). Reads ONLY data already computed by the
// parser (per_symbol_summary / open_positions / trades) — no new math.
// Phase 1 (2026-06) — surfaces the EXACT contract spec used per symbol
// (multiplier + source + verified badge), option metadata, and the
// implied-fee-rate diagnostic (or Delta's-own-Realised-P&L indicator).
const prettySpecSource = (src) => {
  switch (src) {
    case "api_backfill":
    case "products_api": return "Delta products API";
    case "cache": return "Delta products (cached)";
    case "memory": return "Verified spec table";
    case "unresolved": return "Unresolved";
    default: return src || "\u2014";
  }
};

function CsvAuditSummary({ fileId, result }) {
  const [openSym, setOpenSym] = React.useState(null);
  const [openExec, setOpenExec] = React.useState({});
  const toggleExec = (key) => setOpenExec((m) => ({ ...m, [key]: !m[key] }));
  const rows = result?.per_symbol_summary || [];
  const openPositions = result?.open_positions || [];
  const trades = result?.trades || [];
  if (rows.length === 0) return null;
  const money = (v) => (v == null ? "—" : formatMoney(v, "USD"));
  const SideTag = ({ side }) => {
    const isLong = String(side || "").toLowerCase() === "long";
    return (
      <span
        data-testid="td-side-tag"
        className="rounded px-1 py-[1px] text-[9px] font-sans font-bold uppercase tracking-wide"
        style={{
          background: isLong ? "color-mix(in srgb, var(--bt-win) 15%, transparent)" : "color-mix(in srgb, var(--bt-loss) 15%, transparent)",
          color: isLong ? "var(--bt-win)" : "var(--bt-loss)",
        }}
      >
        {isLong ? "LONG" : "SHORT"}
      </span>
    );
  };
  const ExecTable = ({ execs, keyBase }) => (
    <table className="w-full text-[10px] mt-1" data-testid={`td-exec-table-${keyBase}`}>
      <thead>
        <tr className="text-left text-[var(--bt-text-muted)]">
          <th className="px-2 py-[2px]">Role</th>
          <th className="px-2 py-[2px]">Side</th>
          <th className="px-2 py-[2px] text-right">Qty</th>
          <th className="px-2 py-[2px] text-right">Price</th>
          <th className="px-2 py-[2px] text-right">Fee</th>
          <th className="px-2 py-[2px]">Time</th>
        </tr>
      </thead>
      <tbody>
        {(execs || []).map((e, ei) => (
          <tr key={`${keyBase}-ex-${ei}`} data-testid={`td-exec-row-${keyBase}-${ei}`} className="border-t border-[var(--bt-border)]/20">
            <td className="px-2 py-[2px]">
              <span className="rounded px-1 text-[9px] font-semibold uppercase"
                style={{ background: e.role === "open" ? "color-mix(in srgb, var(--bt-accent) 14%, transparent)" : "color-mix(in srgb, var(--bt-text-muted) 18%, transparent)", color: e.role === "open" ? "var(--bt-accent)" : "var(--bt-text-muted)" }}>
                {e.role === "open" ? "OPEN" : "CLOSE"}
              </span>
            </td>
            <td className="px-2 py-[2px] uppercase">{e.side}{e.sl ? " · SL" : ""}</td>
            <td className="px-2 py-[2px] text-right font-mono">{Number(e.qty)}</td>
            <td className="px-2 py-[2px] text-right font-mono">{e.price?.toLocaleString?.() ?? e.price}</td>
            <td className="px-2 py-[2px] text-right font-mono">{e.fee}</td>
            <td className="px-2 py-[2px] font-mono text-[9px]">{(e.time || "").replace("T", " ").slice(0, 19)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div
      data-testid={`csv-audit-summary-${fileId}`}
      className="rounded-lg border border-[var(--bt-border)] bg-[var(--bt-surface)]/50 p-3"
    >
      <div className="mb-2 text-sm font-semibold text-[var(--bt-text)]">
        Per-symbol audit
        <span className="ml-2 text-xs font-normal text-[var(--bt-text-muted)]">
          Tap a symbol to see the orders behind its P&amp;L
        </span>
      </div>

      {/* Phase 3 (FIX 3/7) — OPEN positions: excluded from trade stats, but the
          realized P&L already banked from scale-outs is shown. Expandable to
          the exact opening fills. */}
      {openPositions.map((p, i) => {
        const key = `open-${fileId}-${p.trade_id || i}`;
        const execOpen = !!openExec[key];
        return (
          <div
            key={`openpos-${fileId}-${i}`}
            data-testid={`csv-open-position-${fileId}-${p.symbol}`}
            className="mb-2 rounded-md border border-[var(--bt-warn)]/40 bg-[var(--bt-warn)]/10 px-3 py-2 text-xs"
            style={{ color: "var(--bt-warn)" }}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono font-semibold" style={{ color: "var(--bt-text)" }}>{p.symbol}</span>
              <SideTag side={p.side} />
              <span className="rounded px-1 py-[1px] text-[9px] font-bold uppercase tracking-wide"
                style={{ background: "color-mix(in srgb, var(--bt-warn) 20%, transparent)" }}>OPEN</span>
              {p.trade_id && (
                <span data-testid={`csv-open-trade-id-${fileId}-${p.symbol}`} className="font-mono text-[10px]" style={{ color: "var(--bt-text-muted)" }}>#{p.trade_id}</span>
              )}
            </div>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-[2px] text-[11px]" style={{ color: "var(--bt-text)" }}>
              <span>Open qty: <span className="font-mono font-semibold">{Number(p.open_qty)}</span></span>
              <span>Entry: <span className="font-mono">{p.entry_price?.toLocaleString?.() ?? p.entry_price}</span></span>
              <span>Carried fee: <span className="font-mono font-semibold" data-testid={`csv-open-carried-fee-${fileId}-${p.symbol}`}>{money(p.carried_fee)}</span></span>
              <span>Realized so far: <span className="font-mono font-semibold" style={{ color: (p.realized_so_far || 0) >= 0 ? "var(--bt-win)" : "var(--bt-loss)" }}>{p.pnl_unverified ? "Unverified" : money(p.realized_so_far)}</span></span>
              <span className="italic" style={{ color: "var(--bt-warn)" }}>Unrealized P&amp;L not included</span>
            </div>
            {(p.executions || []).length > 0 && (
              <button
                data-testid={`csv-open-exec-toggle-${fileId}-${p.symbol}`}
                className="mt-1 text-[10px] underline"
                style={{ color: "var(--bt-text-muted)" }}
                onClick={() => toggleExec(key)}
              >
                {execOpen ? "▾ Hide" : "▸ Show"} {p.executions.length} execution{p.executions.length === 1 ? "" : "s"}
              </button>
            )}
            {execOpen && <ExecTable execs={p.executions} keyBase={key} />}
          </div>
        );
      })}

      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid={`csv-audit-table-${fileId}`}>
          <thead>
            <tr className="text-left text-[var(--bt-text-muted)] border-b border-[var(--bt-border)]">
              <th className="px-2 py-1">Symbol</th>
              <th className="px-2 py-1 text-right">Executed Qty</th>
              <th className="px-2 py-1 text-right">Realized P&amp;L</th>
              <th className="px-2 py-1 text-right">Fees</th>
              <th className="px-2 py-1 text-right">Net P&amp;L</th>
              <th className="px-2 py-1 text-right">Open Qty</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const expanded = openSym === r.symbol;
              const symTrades = trades.filter((t) => t.symbol === r.symbol);
              const allUnverified = r.has_unverified && r.trade_count === r.unverified_count;
              return (
                <React.Fragment key={`sym-${fileId}-${r.symbol}`}>
                  <tr
                    data-testid={`csv-audit-row-${fileId}-${r.symbol}`}
                    className="cursor-pointer border-b border-[var(--bt-border)]/40 hover:bg-[var(--bt-surface)]"
                    onClick={() => setOpenSym(expanded ? null : r.symbol)}
                  >
                    <td className="px-2 py-1 font-mono font-semibold align-top">
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="inline-block w-3 text-[var(--bt-text-muted)]">{expanded ? "\u25be" : "\u25b8"}</span>
                        <span>{r.symbol}</span>
                        {r.spec_verified ? (
                          <span
                            data-testid={`csv-spec-verified-${fileId}-${r.symbol}`}
                            className="ml-1 rounded px-1 py-[1px] text-[9px] font-sans font-semibold uppercase tracking-wide"
                            style={{ background: "color-mix(in srgb, var(--bt-win) 15%, transparent)", color: "var(--bt-win)" }}
                            title={`Contract spec verified — multiplier ${r.contract_value} from ${prettySpecSource(r.spec_source)}`}
                          >
                            Verified
                          </span>
                        ) : (
                          <span
                            data-testid={`csv-spec-unverified-${fileId}-${r.symbol}`}
                            className="ml-1 rounded px-1 py-[1px] text-[9px] font-sans font-semibold uppercase tracking-wide"
                            style={{ background: "color-mix(in srgb, #d9a406 18%, transparent)", color: "#d9a406" }}
                            title="No trustworthy contract spec — P&L not calculated for this symbol"
                          >
                            Unverified
                          </span>
                        )}
                      </div>
                      {r.is_option && r.option_label && (
                        <div
                          data-testid={`csv-option-label-${fileId}-${r.symbol}`}
                          className="mt-[2px] ml-4 font-sans text-[10px] font-normal"
                          style={{ color: "var(--bt-accent)" }}
                        >
                          &#9678; {r.option_label}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1 text-right font-mono">{Number(r.executed_qty)}</td>
                    <td className="px-2 py-1 text-right font-mono">{allUnverified ? "Unverified" : money(r.realized_pnl)}</td>
                    <td className="px-2 py-1 text-right font-mono">{money(r.fees)}</td>
                    <td className="px-2 py-1 text-right font-mono" style={{ color: (r.net_pnl || 0) >= 0 ? "var(--bt-win)" : "var(--bt-loss)" }}>
                      {allUnverified ? "Unverified" : money(r.net_pnl)}
                    </td>
                    <td className="px-2 py-1 text-right font-mono" style={{ color: (r.open_qty || 0) > 0 ? "var(--bt-warn)" : undefined }}>
                      {Number(r.open_qty)}
                    </td>
                  </tr>
                  {expanded && (
                    <tr data-testid={`csv-audit-detail-${fileId}-${r.symbol}`}>
                      <td colSpan={6} className="bg-[var(--bt-bg)]/60 px-2 py-2">
                        {/* Phase 1 — exact spec used + P&L-source / fee-rate diagnostic. */}
                        <div
                          data-testid={`csv-spec-strip-${fileId}-${r.symbol}`}
                          className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-[var(--bt-text-muted)]"
                        >
                          <span>
                            Contract multiplier:{" "}
                            <span className="font-mono font-semibold text-[var(--bt-text)]">
                              {r.contract_value == null ? "\u2014" : r.contract_value}
                            </span>
                          </span>
                          <span>
                            Spec source:{" "}
                            <span className="font-semibold text-[var(--bt-text)]">{prettySpecSource(r.spec_source)}</span>
                          </span>
                          {r.is_option && r.option_label && (
                            <span>
                              Option:{" "}
                              <span className="font-semibold" style={{ color: "var(--bt-accent)" }}>{r.option_label}</span>
                            </span>
                          )}
                          {r.delta_pnl_source ? (
                            <span
                              data-testid={`csv-pnl-source-${fileId}-${r.symbol}`}
                              className="inline-flex items-center gap-1 rounded px-1.5 py-[1px] font-medium"
                              style={{ background: "color-mix(in srgb, var(--bt-win) 12%, transparent)", color: "var(--bt-win)" }}
                            >
                              P&amp;L from Delta&rsquo;s own Realised P&amp;L (trusted)
                            </span>
                          ) : r.implied_fee_rate != null ? (
                            <span data-testid={`csv-fee-rate-${fileId}-${r.symbol}`}>
                              Implied fee rate:{" "}
                              <span className="font-mono font-semibold text-[var(--bt-text)]">
                                {(r.implied_fee_rate * 100).toFixed(4)}%
                              </span>
                            </span>
                          ) : null}
                        </div>
                        {r.fee_rate_note && (
                          <div
                            data-testid={`csv-fee-rate-note-${fileId}-${r.symbol}`}
                            className="mb-2 rounded-md border px-2 py-1 text-[11px]"
                            style={{ borderColor: "color-mix(in srgb, var(--bt-warn) 40%, transparent)", background: "color-mix(in srgb, var(--bt-warn) 10%, transparent)", color: "var(--bt-warn)" }}
                          >
                            &#9888; {r.fee_rate_note}
                          </div>
                        )}
                        <table className="w-full text-[11px]">
                          <thead>
                            <tr className="text-left text-[var(--bt-text-muted)]">
                              <th className="px-2 py-1">Trade ID</th>
                              <th className="px-2 py-1">Side</th>
                              <th className="px-2 py-1">Entry</th>
                              <th className="px-2 py-1">Exit</th>
                              <th className="px-2 py-1 text-right">Exec Qty</th>
                              <th className="px-2 py-1 text-right">Multiplier</th>
                              <th className="px-2 py-1 text-right">Gross P&amp;L</th>
                              <th className="px-2 py-1 text-right">Fees</th>
                              <th className="px-2 py-1 text-right">Net P&amp;L</th>
                              <th className="px-2 py-1">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {symTrades.map((t, ti) => {
                              const gross = t.pnl_unverified ? null : ((t.base_currency_pnl || 0) + (t.base_currency_fees || 0));
                              const ekey = `trade-${fileId}-${r.symbol}-${t.trade_id || ti}`;
                              const execOpen = !!openExec[ekey];
                              const nExec = (t.executions || []).length;
                              return (
                                <React.Fragment key={`det-${fileId}-${r.symbol}-${ti}`}>
                                <tr data-testid={`csv-trade-row-${fileId}-${r.symbol}-${ti}`} className="border-t border-[var(--bt-border)]/30">
                                  <td className="px-2 py-1 font-mono">
                                    {nExec > 0 ? (
                                      <button data-testid={`csv-trade-exec-toggle-${ekey}`} className="underline" onClick={() => toggleExec(ekey)}>
                                        {execOpen ? "▾" : "▸"} {t.trade_id || "—"}
                                      </button>
                                    ) : (t.trade_id || "—")}
                                  </td>
                                  <td className="px-2 py-1"><SideTag side={t.side} /></td>
                                  <td className="px-2 py-1 font-mono">{t.entry_price?.toLocaleString?.() ?? t.entry_price}</td>
                                  <td className="px-2 py-1 font-mono">{t.exit_price?.toLocaleString?.() ?? t.exit_price}</td>
                                  <td className="px-2 py-1 text-right font-mono">{Number(t.qty)}</td>
                                  <td className="px-2 py-1 text-right font-mono">{t.contract_multiplier == null ? "\u2014" : t.contract_multiplier}</td>
                                  <td className="px-2 py-1 text-right font-mono">{t.pnl_unverified ? "Unverified" : money(gross)}</td>
                                  <td className="px-2 py-1 text-right font-mono">{money(t.base_currency_fees)}</td>
                                  <td className="px-2 py-1 text-right font-mono" style={{ color: t.pnl_unverified ? "#d9a406" : ((t.base_currency_pnl || 0) >= 0 ? "var(--bt-win)" : "var(--bt-loss)") }}>
                                    {t.pnl_unverified ? "Unverified" : money(t.base_currency_pnl)}
                                  </td>
                                  <td className="px-2 py-1 capitalize">{t.position_status || "closed"}</td>
                                </tr>
                                {execOpen && nExec > 0 && (
                                  <tr data-testid={`csv-trade-exec-detail-${ekey}`}>
                                    <td colSpan={10} className="bg-[var(--bt-bg)]/40 px-3 py-1">
                                      <ExecTable execs={t.executions} keyBase={ekey} />
                                    </td>
                                  </tr>
                                )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ── Upload phase ─────────────────────────────────────────────────────────
// Iter 111 Step 7 — Broker auto-sync CTA banner (user decision #3a). Shown
// above the CSV upload card on the upload phase only. Fetches the current
// connection status once so it can adapt its call-to-action: "Connect a
// broker" for new users, "View auto-sync" for already-connected users.
// Iter 127 — copy genericized from Delta-specific to "your broker" so
// future integrations (Binance, Bybit, etc.) fit the same slot without
// implying a favored provider. Setup link still points to the current
// Delta flow since that's the only connector wired today.
function DeltaSyncBanner() {
  const [conn, setConn] = React.useState(null);
  const [loaded, setLoaded] = React.useState(false);
  React.useEffect(() => {
    let alive = true;
    api.get("/delta-connection")
      .then(({ data }) => { if (alive) { setConn(data); setLoaded(true); } })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);
  if (!loaded) return null;
  const status = conn?.status;
  const isActive = status === "active" || status === "validated";
  const inProgress = conn?.backfill_state === "in_progress";
  const label = isActive
    ? (inProgress ? "Auto-sync running…" : "Auto-sync is on")
    : "Connect your broker for auto-sync";
  const desc = isActive
    ? `Your trades sync live${conn?.api_key_masked ? ` from ${conn.api_key_masked}` : ""}, with an automatic safety-net sync as backup.`
    : "Skip the CSV export. Give a view-only pass once and your trades stream in live.";
  const ctaText = isActive ? "View sync status" : "Set it up (2 min)";
  return (
    <section
      data-testid="trade-doctor-delta-sync-banner"
      className="rounded-sm border px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4"
      style={{
        borderColor: isActive
          ? "color-mix(in srgb, var(--bt-win) 40%, var(--bt-border))"
          : "color-mix(in srgb, var(--bt-accent) 40%, var(--bt-border))",
        background: isActive
          ? "color-mix(in srgb, var(--bt-win) 5%, var(--bt-surface))"
          : "color-mix(in srgb, var(--bt-accent) 6%, var(--bt-surface))",
      }}
    >
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
        style={{ background: isActive
          ? "color-mix(in srgb, var(--bt-win) 18%, var(--bt-surface))"
          : "color-mix(in srgb, var(--bt-accent) 14%, var(--bt-surface))" }}
      >
        {isActive
          ? <CheckCircle2 className="w-5 h-5" style={{ color: "var(--bt-win)" }} />
          : <Zap className="w-5 h-5" style={{ color: "var(--bt-accent)" }} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="bt-label" style={{ color: isActive ? "var(--bt-win)" : "var(--bt-accent)" }}>
          {label}
        </p>
        <p className="text-sm text-[var(--bt-text)] mt-0.5">{desc}</p>
      </div>
      <a
        href="/trade-doctor/connect-delta"
        data-testid="trade-doctor-delta-sync-cta"
        className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold rounded-md px-4 py-2 whitespace-nowrap"
        style={{
          background: "var(--bt-accent)",
          color: "#000",
        }}
      >
        {ctaText} <ArrowRight className="w-4 h-4" />
      </a>
    </section>
  );
}


// ── Upload phase ─────────────────────────────────────────────────────────
function UploadPhase({
  files, defaultBroker, onDefaultBrokerChange, onPickClick, onFileInput,
  onDrop, onDragOver, onDragLeave, dragOver, inputRef, updateRow, removeRow,
  allReady, onAnalyseAll, forceRefresh, setForceRefresh,
}) {
  const instructions = defaultBroker ? BROKER_INSTRUCTIONS[defaultBroker] : null;

  return (
    <>
      {/* Step 1 — default broker (optional) */}
      <section
        data-testid="trade-doctor-broker-card"
        className="bt-card border border-[var(--bt-border)] bg-[var(--bt-surface)] rounded-sm p-6 space-y-4"
      >
        <div className="flex items-start gap-3">
          <StepBadge n={1} />
          <div className="flex-1 space-y-3">
            <div>
              <p className="bt-label">Step 1</p>
              <h2 className="font-heading text-xl mt-0.5">Default broker <span className="text-xs font-normal text-[var(--bt-text-muted)]">(optional — you can override per file)</span></h2>
            </div>
            <div className="max-w-md">
              <Select value={defaultBroker} onValueChange={onDefaultBrokerChange}>
                <SelectTrigger
                  data-testid="trade-doctor-broker-select"
                  className="bg-[var(--bt-surface-2)] border-[var(--bt-border)] text-[var(--bt-text)]"
                >
                  <SelectValue placeholder="Select a default broker…" />
                </SelectTrigger>
                <SelectContent>
                  {BROKERS.map((b) => (
                    <SelectItem
                      key={b}
                      value={b}
                      data-testid={`trade-doctor-broker-option-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      {b}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {instructions && (
              <div
                data-testid="trade-doctor-instructions"
                className="rounded-md border px-4 py-3 text-sm leading-relaxed"
                style={{
                  background: "color-mix(in srgb, var(--bt-accent) 6%, var(--bt-surface-2))",
                  borderColor: "color-mix(in srgb, var(--bt-accent) 35%, var(--bt-border))",
                }}
              >
                <p className="bt-label mb-1" style={{ color: "var(--bt-accent)" }}>
                  How to download your CSV from {defaultBroker}
                </p>
                <p className="text-[var(--bt-text)]">{instructions}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Step 2 — drop / pick files */}
      <section
        data-testid="trade-doctor-upload-card"
        className="bt-card border border-[var(--bt-border)] bg-[var(--bt-surface)] rounded-sm p-6 space-y-4"
      >
        <div className="flex items-start gap-3">
          <StepBadge n={2} />
          <div className="flex-1 space-y-4">
            <div>
              <p className="bt-label">Step 2</p>
              <h2 className="font-heading text-xl mt-0.5">
                Upload one or more CSV files
              </h2>
            </div>

            <div
              data-testid="trade-doctor-dropzone"
              role="button"
              tabIndex={0}
              onClick={onPickClick}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPickClick(); } }}
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              className="rounded-md border-2 border-dashed flex flex-col items-center justify-center gap-3 py-10 px-6 text-center cursor-pointer transition-colors"
              style={{
                borderColor: dragOver ? "var(--bt-accent)" : "var(--bt-border)",
                background: dragOver
                  ? "color-mix(in srgb, var(--bt-accent) 8%, var(--bt-surface))"
                  : "var(--bt-surface)",
              }}
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{ background: "var(--bt-surface-2)" }}
              >
                <Upload className="w-6 h-6" style={{ color: "var(--bt-accent)" }} strokeWidth={2} />
              </div>
              <p className="font-heading text-lg">
                Drop CSVs here, or <span style={{ color: "var(--bt-accent)" }}>browse</span>
              </p>
              <p className="text-xs text-[var(--bt-text-muted)] flex items-center gap-1.5">
                <FileSpreadsheet className="w-3.5 h-3.5" />
                Multi-file · .csv only · up to 10 MB each
              </p>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv,application/vnd.ms-excel"
                multiple
                className="hidden"
                data-testid="trade-doctor-file-input"
                onChange={onFileInput}
              />
            </div>

            {/* File list */}
            {files.length > 0 && (
              <ul
                data-testid="trade-doctor-file-list"
                className="space-y-2"
              >
                {files.map((row) => (
                  <li
                    key={row.id}
                    data-testid={`trade-doctor-file-row-${row.id}`}
                    className="flex flex-col md:flex-row md:items-center gap-3 rounded-md border px-4 py-3"
                    style={{
                      borderColor: row.broker
                        ? "color-mix(in srgb, var(--bt-win) 30%, var(--bt-border))"
                        : "var(--bt-border)",
                      background: "var(--bt-surface-2)",
                    }}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <FileSpreadsheet className="w-4 h-4 shrink-0" style={{ color: "var(--bt-text-muted)" }} />
                      <span className="font-mono text-sm truncate" data-testid="trade-doctor-row-filename">
                        {row.file.name}
                      </span>
                      <span className="text-xs text-[var(--bt-text-muted)] shrink-0">
                        {(row.file.size / 1024).toFixed(1)} KB
                      </span>
                    </div>
                    <div className="md:w-56 w-full">
                      <Select
                        value={row.broker}
                        onValueChange={(v) => updateRow(row.id, { broker: v })}
                      >
                        <SelectTrigger
                          data-testid={`trade-doctor-row-broker-${row.id}`}
                          className="bg-[var(--bt-surface)] border-[var(--bt-border)] text-[var(--bt-text)] h-9"
                        >
                          <SelectValue placeholder="Pick broker…" />
                        </SelectTrigger>
                        <SelectContent>
                          {BROKERS.map((b) => (
                            <SelectItem key={b} value={b}>{b}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <button
                      type="button"
                      data-testid={`trade-doctor-row-remove-${row.id}`}
                      onClick={() => removeRow(row.id)}
                      className="self-end md:self-center inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-[var(--bt-surface)] transition-colors"
                      aria-label={`Remove ${row.file.name}`}
                    >
                      <X className="w-4 h-4" style={{ color: "var(--bt-text-muted)" }} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {files.length > 0 && (
              <div className="flex items-center justify-between gap-3 pt-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-[var(--bt-border)]"
                  onClick={onPickClick}
                  data-testid="trade-doctor-add-more-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add another file
                </Button>
                <label
                  className="inline-flex items-center gap-2 text-xs cursor-pointer select-none"
                  style={{ color: "var(--bt-text-muted)" }}
                  title="Bypass any saved column-mapping cache for these uploads. Useful after a parser update or if an earlier upload mapped columns incorrectly."
                >
                  <input
                    type="checkbox"
                    data-testid="trade-doctor-force-refresh"
                    checked={forceRefresh}
                    onChange={(e) => setForceRefresh(e.target.checked)}
                    className="accent-[var(--bt-accent)]"
                  />
                  Re-detect (skip cache)
                </label>
                <Button
                  data-testid="trade-doctor-analyze-btn"
                  onClick={onAnalyseAll}
                  disabled={!allReady}
                  className="bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Analyse {files.length} {files.length === 1 ? "file" : "files"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      <p className="text-xs text-[var(--bt-text-muted)] max-w-2xl">
        We never share your CSVs. Non-USD P&amp;L is converted using the ECB
        historical rate on each trade's execution date.
      </p>
    </>
  );
}

// ── Results phase ────────────────────────────────────────────────────────
function ResultsPhase({
  files, setFiles, allTrades, filteredTrades, filterOptions, filter, setFilter,
  symbolFilter, setSymbolFilter,
  excludedSymbols, toggleSymbolTrust,
  anyParsing, onStartOver,
  auditState, auditData, auditError, onGenerateAudit, language,
  importState, importResult, onImportToJournal, onGoToJournal,
}) {
  const totalNetPnl = filteredTrades.reduce((s, t) => s + (t.base_currency_pnl || 0), 0);
  const canAudit = !anyParsing && filteredTrades.length > 0 && auditState !== "loading";
  const canImport = !anyParsing && filteredTrades.length > 0 && importState !== "loading";

  // ─── Iter 89 — react-virtuoso powered preview table.
  // ARCHITECTURE RULE (also called out in the iter-89 spec):
  // `filteredTrades` is the single source of truth — exports, imports,
  // audit, totals, and KPI counts all read from it. The TableVirtuoso
  // below only controls what's *rendered*; it never narrows the data
  // that downstream calls see. `_cid` is the stable per-trade id used
  // for click-through (set at parse-aggregation time in the parent).
  const virtuosoRef = React.useRef(null);
  const [highlightedCid, setHighlightedCid] = React.useState(null);
  const highlightTimerRef = React.useRef(null);

  const normSide = (s) => {
    const v = (s || "").toString().trim().toLowerCase();
    if (v === "buy" || v === "long") return "long";
    if (v === "sell" || v === "short") return "short";
    return v;
  };
  const rowDateOf = (t) =>
    (t.exit_time || t.entry_time || t.date || "").toString().slice(0, 10);

  // Locate the best matching trade for a bias-card example by
  // multi-criterion scoring (date+symbol floor; side & abs($pnl)
  // tiebreakers) and return its STABLE `_cid` — virtualization can
  // then look up the index and scroll to it. Field-value matching is
  // only used to FIND the row; the highlight itself is keyed by _cid
  // so duplicate visible rows can never collide.
  const scrollToTrade = (example) => {
    if (!example?.date || !example?.symbol) return;
    const targetDate = String(example.date).slice(0, 10);
    const targetSym = String(example.symbol).toUpperCase();
    const targetSide = normSide(example.side);
    const targetPnl = Number(example.pnl_usd);

    let bestCid = null;
    let bestScore = -1;
    filteredTrades.forEach((t) => {
      const dateMatch = rowDateOf(t) === targetDate;
      const symMatch = String(t.symbol || "").toUpperCase() === targetSym;
      if (!dateMatch || !symMatch) return;
      let score = 2;
      if (targetSide && normSide(t.side) === targetSide) score += 2;
      if (Number.isFinite(targetPnl)) {
        const rowPnl = Number(t.base_currency_pnl || 0);
        if (Math.abs(rowPnl - targetPnl) < 0.011) score += 3;
      }
      if (score > bestScore) {
        bestScore = score;
        bestCid = t._cid;
      }
    });
    if (!bestCid) return;
    const idx = filteredTrades.findIndex((t) => t._cid === bestCid);
    if (idx < 0) return;

    // Iter 90 — Make the trade table visible on the PAGE before asking
    // virtuoso to scroll its INTERNAL viewport. Without this, virtuoso
    // happily scrolls its own scroller to the right offset, but the
    // table itself stays above the user's viewport (they're reading
    // bias cards down the page) and nothing visible happens.
    //
    // We also defensively expand any ancestor <details> that's closed,
    // in case the table ever gets wrapped inside one in future. This
    // mirrors the user's iter-90 spec wording.
    const wrapper = document.querySelector(
      '[data-testid="trade-doctor-preview-table-wrapper"]',
    );
    // Walk up the DOM and open any collapsed <details> ancestor.
    let collapsedAncestorWasOpened = false;
    if (wrapper) {
      let node = wrapper;
      while (node && node !== document.body) {
        if (node.tagName === "DETAILS" && !node.open) {
          node.open = true;
          collapsedAncestorWasOpened = true;
        }
        node = node.parentElement;
      }
    }

    // Clear first so a repeat click on the same example re-triggers the
    // entry animation (React diff wouldn't re-add the class otherwise).
    setHighlightedCid(null);
    if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current);

    // If we just opened a <details>, give the DOM one frame to layout
    // before measuring + scrolling — otherwise scrollIntoView lands on
    // stale geometry.
    const layoutDelay = collapsedAncestorWasOpened ? 60 : 0;
    window.setTimeout(() => {
      // 1. Bring the table into the viewport.
      if (wrapper) {
        wrapper.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      requestAnimationFrame(() => {
        // 2. Apply the highlight via state.
        setHighlightedCid(bestCid);
        // 3. Ask virtuoso to position the matched row centered in the
        //    table's internal scroller. align:'center' so the row sits
        //    in the middle of the 520 px viewport.
        virtuosoRef.current?.scrollToIndex({
          index: idx,
          behavior: "smooth",
          align: "center",
        });
      });
    }, layoutDelay);

    highlightTimerRef.current = window.setTimeout(
      () => setHighlightedCid(null),
      8000,
    );
  };

  // ─── Clear highlight on outside click (per iter-88 spec) ──────────
  React.useEffect(() => {
    if (!highlightedCid) return;
    const onDocClick = (e) => {
      const inTable = e.target.closest && e.target.closest('[data-testid="trade-doctor-preview-table"]');
      const inExampleBtn = e.target.closest && e.target.closest('[data-testid^="bias-card-"][data-testid*="-example-"]');
      if (!inTable && !inExampleBtn) setHighlightedCid(null);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [highlightedCid]);

  // ─── Stable row id used as the React key + data-testid on each
  // virtualized row so tests can still find a row by date/symbol/side.
  const tradeRowKey = (date, symbol, side) =>
    `td-trade-${(date || "").slice(0, 10)}__${(symbol || "").toUpperCase()}__${(side || "").toLowerCase()}`;

  return (
    <>
      {/* Per-file status summary */}
      <section
        data-testid="trade-doctor-status-summary"
        className="bt-card border border-[var(--bt-border)] bg-[var(--bt-surface)] rounded-sm p-6 space-y-3"
      >
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-heading text-xl">File status</h2>
          <Button
            data-testid="trade-doctor-start-over"
            variant="outline"
            size="sm"
            className="border-[var(--bt-border)]"
            onClick={onStartOver}
          >
            Upload more files
          </Button>
        </div>
        <ul className="space-y-2">
          {files.map((row) => (
            <li
              key={row.id}
              className="flex items-center gap-3 text-sm"
              data-testid={`trade-doctor-status-row-${row.id}`}
            >
              <StatusBadge status={row.status} />
              <span className="font-mono truncate flex-1" title={row.file.name}>{row.file.name}</span>
              <span className="text-[var(--bt-text-muted)] text-xs">{row.broker}</span>
              <span className="text-[var(--bt-text)] text-xs min-w-[90px] text-right">
                {row.status === "done" && (
                  <span className="inline-flex items-center gap-1">
                    {row.result?.from_cache && (
                      <span
                        data-testid={`trade-doctor-status-cached-${row.id}`}
                        className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm"
                        style={{
                          background: "color-mix(in srgb, var(--bt-accent) 15%, transparent)",
                          color: "var(--bt-accent)",
                        }}
                        title="Mapping reused from your saved cache — no LLM call needed"
                      >
                        cached
                      </span>
                    )}
                    {`${row.result?.trade_count ?? 0} trade${row.result?.trade_count === 1 ? "" : "s"}`}
                  </span>
                )}
                {row.status === "unsupported" && (
                  <span style={{ color: "var(--bt-warn)" }}>Parser coming soon</span>
                )}
                {row.status === "error" && (
                  <span style={{ color: "var(--bt-loss)" }} title={row.error}>Failed</span>
                )}
                {row.status === "parsing" && (
                  <span className="inline-flex items-center gap-1 text-[var(--bt-text-muted)]">
                    <Loader2 className="w-3 h-3 animate-spin" /> parsing…
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
        {/* Iter 66 — Pairing diagnostics: row-level breakdown for each
            parsed Delta file. Helps audit "why 887 rows became N trades"
            when partial fills + cancelled SLs are common. */}
        {/* Fix 2 — per-symbol audit summary (2a/2b) + open-position notice (2c). */}
        {files.some((f) => f.status === "done" && f.result?.per_symbol_summary?.length > 0) && (
          <div className="mt-4 space-y-3">
            {files
              .filter((f) => f.status === "done" && f.result?.per_symbol_summary?.length > 0)
              .map((f) => (
                <CsvAuditSummary key={`audit-${f.id}`} fileId={f.id} result={f.result} />
              ))}
          </div>
        )}

        {files.some((f) => f.status === "done" && f.result?.pairing_stats) && (
          <div className="mt-3 space-y-2">
            {files
              .filter((f) => f.status === "done" && f.result?.pairing_stats)
              .map((f) => {
                const s = f.result.pairing_stats;
                // Iter 108b — auto-expand row-diagnostics whenever the
                // file has drift/qty warnings so the "Trust this trade"
                // checkbox is visible without a manual click. Testing
                // agent iter38 flagged that the checkbox was hidden
                // inside a collapsed <details> for real users.
                const hasWarnings = Array.isArray(s.quantity_warnings) && s.quantity_warnings.length > 0;
                return (
                    <details
                      key={`stats-${f.id}`}
                      data-testid={`trade-doctor-pairing-stats-${f.id}`}
                      className="rounded-md border border-[var(--bt-border)] bg-[var(--bt-surface)]/40 px-3 py-2 text-xs"
                      open={hasWarnings || undefined}
                    >
                      <summary
                        className="cursor-pointer select-none text-[var(--bt-text-muted)] hover:text-[var(--bt-text)] transition-colors"
                      >
                        <span className="font-mono mr-2">{f.file.name}</span>
                        <span>
                          {s.total_rows ?? 0} rows → {s.trades_produced ?? f.result.trade_count ?? 0} trades
                          {s.unmatched_open_legs > 0 && (
                            <span style={{ color: "var(--bt-warn)" }}>
                              {" "}· {s.unmatched_open_legs} open legs at EOF
                            </span>
                          )}
                          {s.skipped_non_filled > 0 && (
                            <span> · {s.skipped_non_filled} cancelled/non-filled</span>
                          )}
                          {Array.isArray(s.quantity_warnings) && s.quantity_warnings.length > 0 && (
                            <span style={{ color: "var(--bt-warn)" }}>
                              {" "}· ⚠ {s.quantity_warnings.length} unknown-symbol warning
                              {s.quantity_warnings.length === 1 ? "" : "s"}
                            </span>
                          )}
                          {/* Iter 77 — Auto-synthesised header notice.
                              We add this when the user's CSV starts with
                              data (no header row) — common with Sheets
                              pastes or screenshot exports. */}
                          {s.synthesised_header && (
                            <span style={{ color: "var(--bt-accent)" }}>
                              {" "}· ⓘ header row auto-added
                            </span>
                          )}
                          {/* Iter 73 — Per-trade skip telemetry summary chip. */}
                          {(s.blank_rows > 0 || s.shifted_rows > 0 || s.duplicate_rows > 0 || s.shifted_rows_realigned > 0) && (
                            <span style={{ color: "var(--bt-text-muted)" }}>
                              {s.blank_rows > 0 && (
                                <> · {s.blank_rows} blank row{s.blank_rows === 1 ? "" : "s"} skipped</>
                              )}
                              {s.shifted_rows_realigned > 0 && (
                                <> · {s.shifted_rows_realigned} column-shifted row
                                  {s.shifted_rows_realigned === 1 ? "" : "s"} re-aligned</>
                              )}
                              {(s.shifted_rows > 0 || s.duplicate_rows > 0) && (
                                <> · {(s.shifted_rows || 0) + (s.duplicate_rows || 0)} shifted/duplicate row
                                  {((s.shifted_rows || 0) + (s.duplicate_rows || 0)) === 1 ? "" : "s"} skipped</>
                              )}
                            </span>
                          )}
                        </span>
                      </summary>
                      {/* Iter 77 — Synthesised-header explanation banner.
                          Visible inside the expanded diagnostics so users
                          who notice the "header row auto-added" chip can
                          read why. */}
                      {s.synthesised_header && (
                        <div
                          className="mt-2 rounded border px-3 py-2 text-xs"
                          style={{
                            borderColor: "color-mix(in srgb, var(--bt-accent) 40%, transparent)",
                            background: "color-mix(in srgb, var(--bt-accent) 8%, transparent)",
                          }}
                          data-testid={`trade-doctor-synth-header-${f.id}`}
                        >
                          <div className="font-semibold tracking-wider uppercase text-[10px]" style={{ color: "var(--bt-accent)" }}>
                            Header row auto-added
                          </div>
                          <div className="mt-1" style={{ color: "var(--bt-text-muted)" }}>
                            Your CSV started directly with data (no column-name row).
                            We added a placeholder header (<code>col_1, col_2, …</code>) so
                            the AI mapper could identify each column. If anything looks
                            off, click any row in the parsed table to verify.
                          </div>
                        </div>
                      )}
                      {/* Iter 68 — asset-class-agnostic guardrail. Any symbol
                          that fell through to multiplier=1.0 / Forex default
                          surfaces here so the user knows BEFORE trusting P&L. */}
                      {Array.isArray(s.quantity_warnings) && s.quantity_warnings.length > 0 && (
                        <div
                          className="mt-2 pl-2 rounded border px-3 py-2 space-y-2"
                          style={{
                            borderColor: "color-mix(in srgb, var(--bt-warn) 40%, transparent)",
                            background: "color-mix(in srgb, var(--bt-warn) 8%, transparent)",
                            color: "var(--bt-warn)",
                          }}
                          data-testid={`trade-doctor-qty-warnings-${f.id}`}
                        >
                          <div className="font-semibold uppercase tracking-wider text-[10px]">
                            Contract-spec sanity check — P&L may be off
                          </div>
                          {s.quantity_warnings.map((w, i) => {
                            const sym = (w.symbol || "").toUpperCase();
                            const isDrift = typeof w.drift_factor === "number";
                            const excluded = excludedSymbols.has(sym);
                            return (
                              <div key={i} className="text-[var(--bt-text-muted)] flex items-start gap-2">
                                <div className="flex-1">
                                  <span className="font-mono">{w.symbol}</span>{" "}
                                  <span className="opacity-70">({w.broker})</span> — {w.message}
                                </div>
                                {isDrift && (
                                  <label
                                    className="flex items-center gap-1.5 text-[11px] whitespace-nowrap cursor-pointer select-none px-2 py-1 rounded border"
                                    style={{
                                      borderColor: excluded
                                        ? "color-mix(in srgb, var(--bt-loss) 55%, transparent)"
                                        : "color-mix(in srgb, var(--bt-warn) 40%, transparent)",
                                      background: excluded
                                        ? "color-mix(in srgb, var(--bt-loss) 12%, transparent)"
                                        : "transparent",
                                      color: excluded ? "var(--bt-loss)" : "var(--bt-warn)",
                                    }}
                                    title={
                                      excluded
                                        ? `${sym} trades are currently EXCLUDED from the totals, audit and PDF while you verify the contract spec with Delta.`
                                        : `Tick to exclude all ${sym} trades from the totals, audit and PDF until you verify the contract spec with Delta.`
                                    }
                                    data-testid={`trade-doctor-trust-toggle-${sym}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={excluded}
                                      onChange={() => toggleSymbolTrust(sym)}
                                      className="cursor-pointer"
                                    />
                                    {excluded ? "Excluded" : "Exclude from audit"}
                                  </label>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {/* Iter 73 — Per-trade skip telemetry breakdown (only
                          emitted by apply_mapping_per_trade on AI-mapped CSVs
                          where is_per_fill=false; e.g. MT4/MT5/prop-firm). */}
                      {(typeof s.blank_rows === "number" ||
                        typeof s.shifted_rows === "number" ||
                        typeof s.duplicate_rows === "number" ||
                        typeof s.shifted_rows_realigned === "number") && (
                        <div
                          className="mt-2 pl-2 grid grid-cols-2 gap-x-6 gap-y-1 font-mono"
                          data-testid={`trade-doctor-row-skip-stats-${f.id}`}
                        >
                          <div>Blank rows skipped</div>
                          <div className="text-right">{s.blank_rows ?? 0}</div>
                          {typeof s.shifted_rows_realigned === "number" && s.shifted_rows_realigned > 0 && (
                            <>
                              <div title="Rows whose data was shifted right by N blank columns (typically a Google-Sheets copy-paste artefact) and re-aligned by the preprocessor before parsing. Without this fix these rows would be silently dropped.">
                                Column-shifted rows re-aligned
                              </div>
                              <div className="text-right">{s.shifted_rows_realigned}</div>
                            </>
                          )}
                          <div>Shifted / column-offset rows skipped</div>
                          <div className="text-right">{s.shifted_rows ?? 0}</div>
                          <div>Duplicate rows skipped</div>
                          <div className="text-right">{s.duplicate_rows ?? 0}</div>
                          {typeof s.other_skipped === "number" && s.other_skipped > 0 && (
                            <>
                              <div>Other rows skipped (missing required field)</div>
                              <div className="text-right">{s.other_skipped}</div>
                            </>
                          )}
                        </div>
                      )}
                      {/* Deterministic-path stats only show when present
                          (universal mapper currently only emits qty_warnings). */}
                      {typeof s.total_rows === "number" && (
                        <div className="mt-2 pl-2 grid grid-cols-2 gap-x-6 gap-y-1 font-mono">
                          <div>Total rows in CSV</div><div className="text-right">{s.total_rows}</div>
                          <div>Filled / closed (kept)</div><div className="text-right">{s.kept_filled}</div>
                          <div>Cancelled / pending (skipped)</div><div className="text-right">{s.skipped_non_filled}</div>
                          <div>Bad timestamp (skipped)</div><div className="text-right">{s.skipped_bad_time}</div>
                          <div>Missing qty/symbol (skipped)</div><div className="text-right">{s.skipped_no_qty_or_symbol}</div>
                          <div>Stop-loss exits matched</div><div className="text-right">{s.stop_loss_exits}</div>
                          <div>Rows matched into pairs</div><div className="text-right">{s.matched_rows}</div>
                          <div>Unmatched legs (still open at EOF)</div>
                          <div className="text-right" style={s.unmatched_open_legs > 0 ? { color: "var(--bt-warn)" } : {}}>
                            {s.unmatched_open_legs}
                          </div>
                          <div className="border-t border-[var(--bt-border)] pt-1 mt-1 col-span-1">Trades produced</div>
                          <div className="border-t border-[var(--bt-border)] pt-1 mt-1 text-right col-span-1">{s.trades_produced}</div>
                        </div>
                      )}
                    </details>
                );
              })}
          </div>
        )}
      </section>

      {/* Iter 60 — Confirm column mapping panel(s).
          Renders one panel per file whose backend response had
          `needs_confirmation: true`. After the user confirms, we update
          that file's row to `status="done"` with the freshly-parsed trades
          so it flows into the rest of the pipeline (preview / audit /
          import) untouched. */}
      {files
        .filter((f) => f.status === "needs_confirmation" && f.result)
        .map((row) => (
          <ConfirmMappingPanel
            key={`mapping-${row.id}`}
            parseResult={row.result}
            broker={row.broker}
            onConfirmed={(parsed) => {
              const merged = { ...row.result, ...parsed, needs_confirmation: false };
              setFiles((prev) =>
                prev.map((r) => (r.id === row.id ? { ...r, status: "done", result: merged, error: null } : r))
              );
            }}
            onCancel={() => {
              setFiles((prev) => prev.filter((r) => r.id !== row.id));
            }}
          />
        ))}

      {/* Combined preview */}
      {!anyParsing && (
        <section
          data-testid="trade-doctor-preview-card"
          className="bt-card border border-[var(--bt-border)] bg-[var(--bt-surface)] rounded-sm p-6 space-y-4"
        >
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="bt-label" style={{ color: "var(--bt-accent)" }}>Preview</p>
              <h2 className="font-heading text-xl mt-0.5" data-testid="trade-doctor-preview-title">
                {allTrades.length} trade{allTrades.length === 1 ? "" : "s"} found across {files.filter((f) => f.status === "done").length} file{files.filter((f) => f.status === "done").length === 1 ? "" : "s"}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-[var(--bt-text-muted)]" />
              <div className="w-56">
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger
                    data-testid="trade-doctor-filter-select"
                    className="bg-[var(--bt-surface-2)] border-[var(--bt-border)] text-[var(--bt-text)] h-9"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {filterOptions.map((opt) => (
                      <SelectItem
                        key={opt.value}
                        value={opt.value}
                        data-testid={`trade-doctor-filter-option-${opt.value.replace(/[^a-z0-9]+/gi, "-")}`}
                      >
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Summary stats for the filtered slice */}
          {filteredTrades.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
              <Stat
                label="Filtered trades"
                value={filteredTrades.length}
                testId="trade-doctor-stat-count"
              />
              <Stat
                label="Net P&L (USD)"
                value={`$${totalNetPnl.toFixed(2)}`}
                accent={totalNetPnl >= 0 ? "var(--bt-win)" : "var(--bt-loss)"}
                testId="trade-doctor-stat-net-usd"
              />
              <Stat
                label="Brokers"
                value={new Set(filteredTrades.map((t) => t.broker)).size}
                testId="trade-doctor-stat-brokers"
              />
            </div>
          )}

          {/* Iter 108 — "Trust this trade" exclusion summary. Shown when
              the user has opted-out one or more drift-flagged symbols
              via the checkbox in the pairing-stats accordion. Makes it
              impossible to forget you're auditing a filtered slice. */}
          {excludedSymbols.size > 0 && (
            <div
              data-testid="trade-doctor-excluded-symbols-chip"
              className="flex items-center flex-wrap gap-2 text-xs rounded border px-3 py-2"
              style={{
                borderColor: "color-mix(in srgb, var(--bt-loss) 45%, transparent)",
                background: "color-mix(in srgb, var(--bt-loss) 8%, transparent)",
                color: "var(--bt-loss)",
              }}
            >
              <span className="font-semibold uppercase tracking-wider text-[10px]">
                Excluded from audit ({excludedSymbols.size})
              </span>
              {Array.from(excludedSymbols).sort().map((sym) => (
                <button
                  key={sym}
                  type="button"
                  onClick={() => toggleSymbolTrust(sym)}
                  data-testid={`trade-doctor-excluded-chip-${sym}`}
                  className="font-mono px-2 py-0.5 rounded border text-[11px] cursor-pointer hover:opacity-80"
                  style={{
                    borderColor: "color-mix(in srgb, var(--bt-loss) 55%, transparent)",
                    background: "color-mix(in srgb, var(--bt-loss) 15%, transparent)",
                  }}
                  title={`Click to re-include ${sym} trades in the totals and audit.`}
                >
                  {sym} ×
                </button>
              ))}
              <span className="ml-auto text-[var(--bt-text-muted)] normal-case tracking-normal">
                Trades from these symbols are excluded from all totals,
                the audit and the PDF until you re-tick them.
              </span>
            </div>
          )}

          {/* Iter 89 — Symbol filter input (combines with the existing
              broker / asset-class dropdown). Operates on the same
              `filteredTrades` array — there is only one source of truth. */}
          {allTrades.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <label
                htmlFor="td-symbol-filter"
                className="text-xs uppercase tracking-wider text-[var(--bt-text-muted)]"
              >
                Symbol filter
              </label>
              <input
                id="td-symbol-filter"
                data-testid="trade-doctor-symbol-filter"
                type="text"
                value={symbolFilter}
                onChange={(e) => setSymbolFilter(e.target.value)}
                placeholder="Exact symbol (e.g. XAUUSD, ETHUSD) — comma-separate for OR"
                className="flex-1 rounded-sm border border-[var(--bt-border)] bg-[var(--bt-surface)] px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[var(--bt-accent)]"
              />
              {symbolFilter && (
                <button
                  type="button"
                  data-testid="trade-doctor-symbol-filter-clear"
                  onClick={() => setSymbolFilter("")}
                  className="text-xs text-[var(--bt-text-muted)] hover:text-[var(--bt-text)] underline"
                >
                  Clear
                </button>
              )}
            </div>
          )}

          {/* Iter 89 — Virtualized preview table powered by react-virtuoso.
              Renders every row in `filteredTrades` (no 200-row cap) and
              uses the library's `scrollToIndex` for click-through.
              IMPORTANT: this only controls what's *rendered*. All exports,
              imports, audit, and totals continue to read from the full
              `filteredTrades` array above. */}
          <div
            className="rounded-md border border-[var(--bt-border)] overflow-hidden"
            data-testid="trade-doctor-preview-table-wrapper"
          >
            {filteredTrades.length === 0 ? (
              <div
                className="text-center py-8 text-[var(--bt-text-muted)] text-sm"
                data-testid="trade-doctor-preview-empty"
              >
                No trades match this filter.
              </div>
            ) : (
              <TableVirtuoso
                ref={virtuosoRef}
                data={filteredTrades}
                computeItemKey={(_idx, item) => item._cid}
                style={{ height: 520 }}
                data-testid="trade-doctor-preview-table"
                components={{
                  // eslint-disable-next-line react/no-unstable-nested-components
                  Table: (props) => (
                    <table
                      {...props}
                      data-testid="trade-doctor-preview-table"
                      className="w-full text-sm border-collapse"
                    />
                  ),
                  // eslint-disable-next-line react/no-unstable-nested-components
                  TableRow: (props) => {
                    const t = filteredTrades[props["data-index"]];
                    const isHi = t && highlightedCid === t._cid;
                    return (
                      <tr
                        {...props}
                        data-testid={t ? tradeRowKey(rowDateOf(t), t.symbol, t.side) : undefined}
                        data-cid={t?._cid}
                        className={`border-t border-[var(--bt-border)] hover:bg-[var(--bt-surface-2)] transition-colors ${
                          isHi ? "bt-trade-row-highlight" : ""
                        }`}
                      />
                    );
                  },
                }}
                // eslint-disable-next-line react/no-unstable-nested-components
                fixedHeaderContent={() => (
                  <tr
                    className="text-[var(--bt-text-muted)] text-xs uppercase tracking-wider"
                    style={{ background: "var(--bt-surface-2)" }}
                  >
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Symbol</th>
                    <th className="text-left px-3 py-2">Broker</th>
                    <th className="text-left px-3 py-2">Side</th>
                    <th className="text-right px-3 py-2">Entry</th>
                    <th className="text-right px-3 py-2">Exit</th>
                    <th className="text-right px-3 py-2">P&amp;L (native)</th>
                    <th className="text-right px-3 py-2">P&amp;L (USD)</th>
                  </tr>
                )}
                // eslint-disable-next-line react/no-unstable-nested-components
                itemContent={(_idx, t) => (
                  <>
                    <td className="px-3 py-2 font-mono text-xs text-[var(--bt-text-muted)] whitespace-nowrap">
                      {rowDateOf(t) || "—"}
                    </td>
                    <td className="px-3 py-2 font-mono">{t.symbol}</td>
                    <td className="px-3 py-2 text-[var(--bt-text-muted)] text-xs">{t.broker}</td>
                    <td className="px-3 py-2">
                      <span
                        className="text-xs uppercase tracking-wider"
                        style={{
                          color: normSide(t.side) === "long" ? "var(--bt-win)" : "var(--bt-loss)",
                        }}
                      >
                        {t.side}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-right">{t.entry_price?.toLocaleString()}</td>
                    <td className="px-3 py-2 font-mono text-right">{t.exit_price?.toLocaleString()}</td>
                    <td
                      className="px-3 py-2 font-mono text-right"
                      style={{ color: t.pnl_unverified ? "#d9a406" : ((t.pnl_native || 0) >= 0 ? "var(--bt-win)" : "var(--bt-loss)") }}
                      title={t.pnl_unverified ? (t.pnl_unverified_reason || "Contract specification unverified — P&L not calculated") : undefined}
                    >
                      {t.pnl_unverified ? "Unverified" : `${t.native_currency} ${(t.pnl_native || 0).toFixed(2)}`}
                    </td>
                    <td
                      className="px-3 py-2 font-mono text-right"
                      style={{ color: t.pnl_unverified ? "#d9a406" : ((t.base_currency_pnl || 0) >= 0 ? "var(--bt-win)" : "var(--bt-loss)") }}
                      title={t.pnl_unverified ? (t.pnl_unverified_reason || "Contract specification unverified — P&L not calculated") : undefined}
                    >
                      {t.pnl_unverified ? "P&L not calculated" : `$${(t.base_currency_pnl || 0).toFixed(2)}`}
                    </td>
                  </>
                )}
              />
            )}
          </div>
          {filteredTrades.length > 0 && (
            <p className="text-xs text-[var(--bt-text-muted)]">
              Showing all {filteredTrades.length.toLocaleString()} {filteredTrades.length === 1 ? "trade" : "trades"}
              {symbolFilter ? ` matching "${symbolFilter.toUpperCase()}"` : ""}
              {filteredTrades.length !== allTrades.length ? ` (of ${allTrades.length.toLocaleString()} total)` : ""}.
            </p>
          )}
        </section>
      )}

      {/* Iter 43 — bulk-import to journal */}
      {!anyParsing && filteredTrades.length > 0 && (
        <ImportPanel
          state={importState}
          result={importResult}
          canImport={canImport}
          filteredCount={filteredTrades.length}
          onImport={() => onImportToJournal(filteredTrades)}
          onGoToJournal={onGoToJournal}
        />
      )}

      {/* Iter 42 — AI audit panel */}
      {!anyParsing && filteredTrades.length > 0 && (
        <AuditPanel
          state={auditState}
          data={auditData}
          error={auditError}
          canAudit={canAudit}
          filteredCount={filteredTrades.length}
          filteredTrades={filteredTrades}
          onGenerate={() => onGenerateAudit(filteredTrades)}
          currentLanguage={language}
          onRegenerate={() => onGenerateAudit(filteredTrades, language)}
          onExampleClick={scrollToTrade}
        />
      )}
    </>
  );
}

// ── Bulk-import panel (Iter 43, refined Iter 48) ─────────────────────────
// Single unified card. The button toggles between three states:
//   idle    → "Save to Journal"
//   loading → "Importing…" (disabled)
//   done    → "✓ Saved to journal" (disabled) + small "View journal" link
// No raw count in the button label per spec — count lives in the subtext.
function ImportPanel({ state, result, canImport, filteredCount, onImport, onGoToJournal }) {
  const done = state === "done" && result;
  const loading = state === "loading";
  const inserted = done ? result.inserted : 0;
  const skipped = done ? result.skipped_existing : 0;
  const allDup = done && inserted === 0 && skipped > 0;

  return (
    <section
      data-testid={done ? "trade-doctor-import-done" : "trade-doctor-import-cta"}
      className="bt-card border rounded-sm p-5"
      style={{
        borderColor: done
          ? "color-mix(in srgb, var(--bt-win) 40%, var(--bt-border))"
          : "var(--bt-border)",
        background: done
          ? "color-mix(in srgb, var(--bt-win) 5%, var(--bt-surface))"
          : "var(--bt-surface)",
      }}
    >
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div
          className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: "var(--bt-surface-2)" }}
        >
          {done ? (
            <CheckCircle2 className="w-5 h-5" style={{ color: "var(--bt-win)" }} />
          ) : (
            <Save className="w-5 h-5" style={{ color: "var(--bt-accent)" }} />
          )}
        </div>

        <div className="flex-1 space-y-0.5">
          <p className="font-heading text-base" data-testid={done ? "trade-doctor-import-result" : undefined}>
            {done
              ? (allDup
                  ? "All trades were already in your journal."
                  : `${inserted} trade${inserted === 1 ? "" : "s"} added to your journal.`)
              : "Save to your journal"}
          </p>
          <p className="text-xs text-[var(--bt-text-muted)]">
            {done ? (
              <>
                {skipped > 0 && <>{skipped} skipped (already imported). </>}
                Imported trades show up on your dashboard immediately.
              </>
            ) : (
              <>
                Add the {filteredCount} trade{filteredCount === 1 ? "" : "s"} in
                view to your BrainTRADES journal so they show up on the
                dashboard and analytics. Duplicates are skipped automatically.
              </>
            )}
          </p>
        </div>

        <div className="flex flex-col items-end gap-1.5">
          <Button
            data-testid="trade-doctor-import-btn"
            onClick={done ? undefined : onImport}
            disabled={done || !canImport}
            className={
              done
                ? "bg-[var(--bt-surface-2)] text-[var(--bt-win)] border border-[color-mix(in_srgb,var(--bt-win)_40%,var(--bt-border))] font-semibold shrink-0 cursor-not-allowed disabled:opacity-100"
                : "bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-semibold shrink-0"
            }
          >
            {done ? (
              <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Saved to journal</>
            ) : loading ? (
              <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Importing…</>
            ) : (
              <><Save className="w-4 h-4 mr-1.5" /> Save to Journal</>
            )}
          </Button>
          {done && (
            <button
              type="button"
              data-testid="trade-doctor-go-to-journal"
              onClick={onGoToJournal}
              className="text-xs underline-offset-2 hover:underline"
              style={{ color: "var(--bt-text-muted)" }}
            >
              View journal →
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

// ── AI audit panel ───────────────────────────────────────────────────────
function AuditPanel({ state, data, error, canAudit, filteredCount, filteredTrades, onGenerate, currentLanguage, onRegenerate, onExampleClick }) {
  // Initial state — show a CTA card prompting the user to generate the audit.
  if (state === "idle") {
    return (
      <section
        data-testid="trade-doctor-audit-cta"
        className="bt-card border border-[var(--bt-border)] bg-[var(--bt-surface)] rounded-sm p-6"
        style={{
          background: "linear-gradient(135deg, color-mix(in srgb, var(--bt-accent) 4%, var(--bt-surface)) 0%, var(--bt-surface) 60%)",
        }}
      >
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
          <div
            className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center"
            style={{ background: "var(--bt-surface-2)" }}
          >
            <Sparkles className="w-5 h-5" style={{ color: "var(--bt-accent)" }} />
          </div>
          <div className="flex-1 space-y-1">
            <p className="bt-label" style={{ color: "var(--bt-accent)" }}>Step 3 · AI audit</p>
            <h2 className="font-heading text-xl">Ready when you are</h2>
            <p className="text-sm text-[var(--bt-text-muted)] max-w-xl">
              Generate an honest, personalised audit on the {filteredCount}{" "}
              trade{filteredCount === 1 ? "" : "s"} currently in view —
              strengths, leaks, behavioural patterns, and what to fix first.
            </p>
          </div>
          <Button
            data-testid="trade-doctor-audit-btn"
            onClick={onGenerate}
            disabled={!canAudit}
            className="bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-semibold shrink-0"
          >
            <Sparkles className="w-4 h-4 mr-1.5" /> Generate audit
          </Button>
        </div>
      </section>
    );
  }

  if (state === "loading") {
    return <AuditLoading />;
  }

  if (state === "error") {
    return (
      <section
        data-testid="trade-doctor-audit-error"
        className="bt-card border rounded-sm p-6 space-y-3"
        style={{
          borderColor: "color-mix(in srgb, var(--bt-loss) 50%, var(--bt-border))",
          background: "color-mix(in srgb, var(--bt-loss) 6%, var(--bt-surface))",
        }}
      >
        <div className="flex items-center gap-2">
          <AlertOctagon className="w-5 h-5" style={{ color: "var(--bt-loss)" }} />
          <h3 className="font-heading text-lg">Audit failed</h3>
        </div>
        <p className="text-sm text-[var(--bt-text-muted)]">{error || "Unknown error."}</p>
        <Button
          variant="outline"
          size="sm"
          className="border-[var(--bt-border)]"
          onClick={onGenerate}
          data-testid="trade-doctor-audit-retry"
        >
          Try again
        </Button>
      </section>
    );
  }

  // Done — render the structured report.
  const report = data?.report || {};
  const score = Number(report.overall_score ?? 0);
  const auditLang = data?.language || "en";
  // Iter 53 — surface a "Regenerate in <other lang>" CTA when the user's
  // current language toggle no longer matches what was generated. We never
  // auto-regenerate (LLM tokens cost) — keep it explicit.
  const showRegenerate = currentLanguage && auditLang && currentLanguage !== auditLang;
  const otherLangLabel = currentLanguage === "hi" ? "Hinglish" : "English";
  const scoreColor =
    score >= 80 ? "var(--bt-win)"
      : score >= 60 ? "var(--bt-accent)"
      : score >= 40 ? "var(--bt-warn)"
      : "var(--bt-loss)";

  // Iter 81 — Hero "Top 3 Findings". Synthesised purely from existing
  // audit data — no new LLM call. Picks:
  //   • Biggest Risk     = highest-impact leak (impact: 'high' first,
  //                        else first in array)
  //   • Biggest Strength = first strength
  //   • This Week's Fix  = first `fix_first` action
  // Self-referential only — never any cross-user / percentile / leaderboard
  // framing (the user explicitly banned that in iter-81).
  const _leaks = Array.isArray(report.leaks) ? report.leaks : [];
  const _strengths = Array.isArray(report.strengths) ? report.strengths : [];
  const _fixes = Array.isArray(report.fix_first) ? report.fix_first : [];
  const biggestRisk =
    _leaks.find((l) => (l?.impact || "").toLowerCase() === "high") || _leaks[0] || null;
  const biggestStrength = _strengths[0] || null;
  const thisWeeksFix = _fixes[0] || null;

  return (
    <section
      data-testid="trade-doctor-audit-report"
      className="space-y-6"
    >
      {/* Iter 93 — Download Trade Doctor Report PDF. Bundles the AI audit
          (Health Score, Doctor Summary, Top 3 Findings, Fix These First,
          Behavioural Patterns with citations, Strengths) into a print-
          ready PDF via POST /api/trade-doctor/report.pdf. */}
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-[var(--bt-text-muted)]">
          Audit generated in <strong>{auditLang === "hi" ? "Hinglish" : "English"}</strong>.
        </p>
        <Button
          data-testid="trade-doctor-report-pdf-btn"
          variant="outline"
          size="sm"
          className="border-[var(--bt-border)] shrink-0"
          onClick={async () => {
            try {
              const res = await api.post(
                "/trade-doctor/report.pdf",
                { aggregates: data?.aggregates || null, report, language: auditLang },
                { responseType: "blob" },
              );
              const url = window.URL.createObjectURL(res.data);
              const a = document.createElement("a");
              a.href = url;
              a.download = `braintrades_trade_doctor_report_${new Date()
                .toISOString()
                .slice(0, 10)}.pdf`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);
              toast.success("Report PDF downloaded");
            } catch (e) {
              // Iter 100b — surface the actual backend error so we
              // never silently bury "PDF export failed" again. When
              // responseType=blob and the server returns JSON
              // (validation error / 5xx), axios hands us a Blob —
              // parse it for the real `detail` message.
              let detail = e?.message || "Unknown error";
              try {
                if (e?.response?.data instanceof Blob) {
                  const txt = await e.response.data.text();
                  try {
                    const obj = JSON.parse(txt);
                    detail = obj?.detail || obj?.message || txt;
                  } catch {
                    detail = txt;
                  }
                } else if (e?.response?.data?.detail) {
                  detail = e.response.data.detail;
                }
              } catch {
                /* fallthrough — fall back to the message above */
              }
              // Show the user a usable error in the toast.
              const code = e?.response?.status ? `${e.response.status}: ` : "";
              toast.error(`PDF export failed — ${code}${detail}`);
              // Surface the full error to console for support diagnostics.
              console.error("Trade Doctor PDF export failed", { status: e?.response?.status, detail, error: e });
            }
          }}
        >
          <FileDown className="w-3.5 h-3.5 mr-1.5" />
          Download Report PDF
        </Button>
      </div>

      {/* Iter 53 — Language mismatch CTA. Shown only when current toggle
          differs from what was used to generate the audit. Subtle, single
          line, with one "Regenerate in <X>" button. */}
      {showRegenerate && (
        <div
          data-testid="audit-regenerate-cta"
          className="flex items-center justify-between gap-3 rounded-sm border px-4 py-3"
          style={{
            background: "color-mix(in srgb, var(--bt-accent) 6%, var(--bt-surface))",
            borderColor: "color-mix(in srgb, var(--bt-accent) 35%, var(--bt-border))",
          }}
        >
          <p className="text-sm text-[var(--bt-text)]">
            This audit was generated in{" "}
            <strong>{auditLang === "hi" ? "Hinglish" : "English"}</strong>.
            Switch the tone to <strong>{otherLangLabel}</strong>?
          </p>
          <Button
            data-testid="audit-regenerate-btn"
            size="sm"
            variant="outline"
            onClick={onRegenerate}
            className="border-[var(--bt-border)] shrink-0"
          >
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            Regenerate in {otherLangLabel}
          </Button>
        </div>
      )}

      {/* D7b — Free-credit banner. Elite responses omit the
          `free_credit` block so this only renders once, right after
          a Free user's one-lifetime audit lands. */}
      {data?.free_credit?.was_free_credit && (
        <div
          data-testid="doctor-free-credit-banner"
          className="border border-dashed border-[var(--bt-accent)] rounded-sm p-4 flex items-start gap-3"
        >
          <Sparkles className="w-4 h-4 mt-0.5 text-[var(--bt-accent)] flex-shrink-0" />
          <div className="text-sm space-y-1">
            <p className="font-heading text-base">This was your free Trade Doctor audit</p>
            <p className="text-[var(--bt-text-muted)]">
              One lifetime free per account. Elite unlocks unlimited
              audits — regenerate any time your data changes.
            </p>
          </div>
        </div>
      )}

      {/* Iter 81/82/83 — Trade Health Report hero (extracted to
          ./TradeDoctor/HeroSection.jsx in iter-83). */}
      <TradeHealthReportHero
        report={report}
        score={score}
        scoreColor={scoreColor}
        biggestRisk={biggestRisk}
        biggestStrength={biggestStrength}
        thisWeeksFix={thisWeeksFix}
      />

      {/* Iter 81 — Full Report (collapsed by default). Holds everything
          that used to render flat below the hero in iters 32-80:
          Strengths grid, Leaks grid, Patterns, Fix First, Behavioral
          cards. Iter 108 — default state: OPEN. Previously collapsed
          by default, which hid ~⅔ of the audit (7 behavioural bias
          cards, top-3 strengths grid, research citation chips) below
          the fold. Every QA pass since iter35 has flagged this. */}
      <details
        open
        data-testid="trade-doctor-full-report"
        className="rounded-sm border border-[var(--bt-border)] overflow-hidden"
      >
        <summary
          className="cursor-pointer select-none px-4 py-3 flex items-center justify-between hover:bg-[var(--bt-surface-2)]"
          style={{ listStyle: "none" }}
        >
          <span className="font-heading text-base">View Full Report</span>
          <span className="text-xs text-[var(--bt-text-muted)]">
            {(_strengths.length + _leaks.length + (report.patterns?.length || 0))} insights · expand ↓
          </span>
        </summary>
        <div className="p-4 space-y-6 border-t border-[var(--bt-border)]">
          {/* Iter 82 — "Fix These First" card section. Replaces the
              compact Leaks list with a richer card format: each leak
              now shows Title, ₹/$ Impact, Why, Action, and a CTA
              challenge button. Cards are sorted by severity (high
              first) so the most painful leak is at the top. */}
          <FixTheseFirstCards
            leaks={report.leaks}
            testid="audit-leaks"
          />

          {/* Iter 82 — Strengths card section. Each card shows a
              user-data-cited evidence sentence plus a short encouraging
              note. */}
          <StrengthsCards
            strengths={report.strengths}
            testid="audit-strengths"
          />

          {/* Iter 104 — Strengths diagnostic drawer. Lets the user
              click and see EXACTLY what (symbol, side) buckets the
              backend computed from their parsed trades, so a
              missing or duplicated strength card can be debugged
              in one click instead of via curl + token. */}
          <StrengthsDiagnosticDrawer
            trades={filteredTrades}
            testid="strengths-diagnostic-drawer"
          />

          {/* Iter 84 — Behavioural Patterns cards. Restructured into the
              Healthy/Moderate/Severe + Evidence + Impact + Action
              presentation. Citations are hidden behind a "ⓘ
              Research-backed" toggle on each card. Sits directly under
              the Strengths section per the iter-84 spec. */}
          {data?.aggregates?.behavioral && (
            <BehavioralBiasCards
              behavioral={data.aggregates.behavioral}
              language={currentLanguage || "en"}
              onExampleClick={onExampleClick}
            />
          )}

          {/* Patterns */}
          <ReportList
            testid="audit-patterns"
            icon={<Eye className="w-5 h-5" />}
            accent="var(--bt-accent)"
            title="Patterns We Spotted"
            items={report.patterns}
            emptyMsg="No repeating patterns detected yet."
          />

          {/* Fix first */}
          <FixFirst
            items={report.fix_first}
            testid="audit-fix-first"
          />
        </div>
      </details>
    </section>
  );
}

function ReportList({ testid, icon, accent, title, items, emptyMsg, showImpact }) {
  const list = Array.isArray(items) ? items : [];
  return (
    <div
      data-testid={testid}
      className="bt-card border border-[var(--bt-border)] bg-[var(--bt-surface)] rounded-sm p-5 space-y-3"
    >
      <div className="flex items-center gap-2">
        <span style={{ color: accent }}>{icon}</span>
        <h3 className="font-heading text-base">{title}</h3>
        <span className="text-xs text-[var(--bt-text-muted)] ml-auto">
          {list.length} {list.length === 1 ? "item" : "items"}
        </span>
      </div>
      {list.length === 0 ? (
        <p className="text-sm text-[var(--bt-text-muted)]">{emptyMsg}</p>
      ) : (
        <ul className="space-y-3">
          {list.map((it, i) => (
            <li
              key={i}
              className="rounded-md border border-[var(--bt-border)] bg-[var(--bt-surface-2)] p-3 space-y-1"
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold leading-snug">{it?.title}</p>
                {showImpact && it?.impact && (
                  <span
                    className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border whitespace-nowrap"
                    style={{
                      color:
                        it.impact === "high" ? "var(--bt-loss)"
                          : it.impact === "moderate" ? "var(--bt-warn)"
                          : "var(--bt-text-muted)",
                      borderColor: "var(--bt-border)",
                      background: "var(--bt-surface)",
                    }}
                  >
                    {it.impact}
                  </span>
                )}
              </div>
              <p className="text-xs leading-relaxed" style={{ color: "var(--bt-text-muted)" }}>
                {it?.detail}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FixFirst({ items, testid }) {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return null;
  return (
    <div
      data-testid={testid}
      className="bt-card border rounded-sm p-5 space-y-3"
      style={{
        borderColor: "color-mix(in srgb, var(--bt-accent) 35%, var(--bt-border))",
        background: "color-mix(in srgb, var(--bt-accent) 5%, var(--bt-surface))",
      }}
    >
      <div className="flex items-center gap-2">
        <Target className="w-5 h-5" style={{ color: "var(--bt-accent)" }} />
        <h3 className="font-heading text-base">Fix These First</h3>
      </div>
      <ol className="space-y-3">
        {list
          .slice()
          .sort((a, b) => (a.priority ?? 99) - (b.priority ?? 99))
          .map((it, i) => (
            <li key={i} className="flex items-start gap-3">
              <span
                className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold"
                style={{ background: "var(--bt-accent)", color: "#000" }}
              >
                {it.priority ?? i + 1}
              </span>
              <div className="flex-1 space-y-1">
                <p className="text-sm font-semibold leading-snug">{it.action}</p>
                <p className="text-xs leading-relaxed" style={{ color: "var(--bt-text-muted)" }}>
                  {it.why}
                </p>
              </div>
            </li>
          ))}
      </ol>
    </div>
  );
}

// ── Tiny building blocks ─────────────────────────────────────────────────
function StepBadge({ n }) {
  return (
    <span
      className="flex shrink-0 w-7 h-7 rounded-full items-center justify-center text-xs font-semibold"
      style={{ background: "var(--bt-surface-2)", color: "var(--bt-text)" }}
    >
      {n}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { icon: <span className="w-2 h-2 rounded-full bg-zinc-500 inline-block" />, color: "var(--bt-text-muted)" },
    parsing: { icon: <Loader2 className="w-3.5 h-3.5 animate-spin" />, color: "var(--bt-text-muted)" },
    done: { icon: <CheckCircle2 className="w-3.5 h-3.5" />, color: "var(--bt-win)" },
    needs_confirmation: { icon: <AlertTriangle className="w-3.5 h-3.5" />, color: "var(--bt-warn)" },
    unsupported: { icon: <AlertTriangle className="w-3.5 h-3.5" />, color: "var(--bt-warn)" },
    error: { icon: <AlertTriangle className="w-3.5 h-3.5" />, color: "var(--bt-loss)" },
  };
  const { icon, color } = map[status] || map.pending;
  return (
    <span
      className="inline-flex items-center justify-center w-6 h-6 rounded-full"
      style={{ color }}
    >
      {icon}
    </span>
  );
}

function Stat({ label, value, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-md border border-[var(--bt-border)] bg-[var(--bt-surface-2)] px-3 py-2"
    >
      <p className="bt-label text-[10px]">{label}</p>
      <p className="font-heading text-lg mt-0.5" style={{ color: accent || "var(--bt-text)" }}>
        {value}
      </p>
    </div>
  );
}

// ── Iter 48: AI audit loading screen ────────────────────────────────────
// Doctor-tone, rotating subtext. No model name shown. Each message stays
// on screen for ~6s; the user only ever sees 3-6 of the 5 during a typical
// 15-40s audit, which keeps the loading feeling alive without being noisy.
const AUDIT_LOADING_MESSAGES = [
  "Checking your vital signs…",
  "Measuring win vs loss ratio…",
  "Spotting emotional patterns…",
  "Diagnosing your trading habits…",
  "Writing your prescription…",
];
const AUDIT_LOADING_INTERVAL_MS = 6000;

function AuditLoading() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setIdx((i) => Math.min(i + 1, AUDIT_LOADING_MESSAGES.length - 1));
    }, AUDIT_LOADING_INTERVAL_MS);
    return () => clearInterval(t);
  }, []);
  return (
    <section
      data-testid="trade-doctor-audit-loading"
      className="bt-card border border-dashed rounded-sm p-10 flex flex-col items-center justify-center gap-3 text-center"
      style={{
        borderColor: "color-mix(in srgb, var(--bt-accent) 45%, var(--bt-border))",
        background: "color-mix(in srgb, var(--bt-accent) 4%, var(--bt-surface))",
      }}
    >
      <Loader2 className="w-10 h-10 animate-spin" style={{ color: "var(--bt-accent)" }} />
      <p className="font-heading text-lg" data-testid="trade-doctor-audit-loading-title">
        Your Trade Doctor is running a checkup…
      </p>
      <p
        key={idx}
        data-testid="trade-doctor-audit-loading-subtext"
        className="text-sm text-[var(--bt-text-muted)] max-w-md transition-opacity duration-500"
        style={{ minHeight: 22 }}
      >
        {AUDIT_LOADING_MESSAGES[idx]}
      </p>
    </section>
  );
}
